export type SetType = 'warmup' | 'work' | 'drop' | 'failure' | string

export type PreviousSet = {
  exercise_id?: string | null
  exercise_name_snapshot?: string | null
  set_type: SetType
  weight?: number | null
  reps?: number | null
  duration_seconds?: number | null
  distance_meters?: number | null
  source_day_id?: string | null
  source_day_index?: number | null
  completed_at?: string | null
}

export type PreviousValueContext = {
  exerciseId?: string | null
  exerciseName: string
  setType: SetType
  sourceDayId?: string | null
  sourceDayIndex?: number | null
}

function sameExercise(set: PreviousSet, context: PreviousValueContext) {
  if (context.exerciseId && set.exercise_id) return set.exercise_id === context.exerciseId
  return set.exercise_name_snapshot === context.exerciseName
}

function samePlanDay(set: PreviousSet, context: PreviousValueContext) {
  if (context.sourceDayId && set.source_day_id) return set.source_day_id === context.sourceDayId
  if (context.sourceDayIndex != null && set.source_day_index != null) return set.source_day_index === context.sourceDayIndex
  return context.sourceDayId == null && context.sourceDayIndex == null
}

/**
 * Product invariant:
 * previous values never cross set types and, where a plan-day context exists,
 * they never leak between different plan days using the same exercise.
 */
export function findPreviousComparableSet(history: PreviousSet[], context: PreviousValueContext) {
  return history
    .filter((set) => sameExercise(set, context))
    .filter((set) => set.set_type === context.setType)
    .filter((set) => samePlanDay(set, context))
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))[0] ?? null
}

/**
 * For an exercise without comparable history, the first confirmed set may
 * seed following comparable sets. It intentionally does not seed warm-up
 * values into work sets or vice versa.
 */
export function seedFollowingComparableSets<T extends { set_type: SetType; weight?: number | null; reps?: number | null }>(
  sets: T[],
  confirmedIndex: number,
): T[] {
  const confirmed = sets[confirmedIndex]
  if (!confirmed) return sets

  return sets.map((set, index) => {
    if (index <= confirmedIndex || set.set_type !== confirmed.set_type) return set
    return {
      ...set,
      weight: set.weight ?? confirmed.weight ?? null,
      reps: set.reps ?? confirmed.reps ?? null,
    }
  })
}
